'use client';

import { useMemo, useState } from 'react';
import {
	Area,
	AreaChart,
	Brush,
	CartesianGrid,
	ReferenceArea,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts';

export type TelemetryRange = 'day' | 'week' | 'month' | 'custom';

export interface TelemetryPoint {
	/** X value — ISO timestamp of the hour bucket */
	hour: string;
	/** Y value — kWh in the hour */
	value: number;
	/** Provenienza visibile nel tooltip, senza aggiungere una seconda serie. */
	sourceLabel?: string;
}

export interface TelemetryChartProps {
	data: TelemetryPoint[];
	/** Stroke/fill accent (cyan production, purple consumption…) */
	accent?: string;
	/** Y-axis unit suffix, e.g. ' kWh' */
	unit?: string;
	/** Tooltip series label */
	seriesLabel?: string;
	loading?: boolean;
	emptyHint?: string;
	/** Selected time range preset */
	range: TelemetryRange;
	customFrom?: string;
	customTo?: string;
	onRangeChange: (range: TelemetryRange, opts?: { from?: string; to?: string }) => void;
	height?: number;
}

const RANGE_LABELS: { value: TelemetryRange; label: string }[] = [
	{ value: 'day', label: 'Giorno' },
	{ value: 'week', label: 'Settimana' },
	{ value: 'month', label: 'Mese' },
	{ value: 'custom', label: 'Custom' },
];

function fmt(iso: string, range: TelemetryRange): string {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return iso;
	const hh = `${String(d.getHours()).padStart(2, '0')}:00`;
	if (range === 'day') return hh;
	const dm = `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
	return range === 'week' ? `${dm} ${hh}` : dm;
}

const inputCls =
	'rounded-md border border-border bg-background px-2 py-1 text-[12px] text-text-primary focus:border-accent focus:outline-none';

// Recharts renders every datum (plus a full-series Brush preview), so wide
// ranges — a month is ~720 hourly points, a multi-year custom span tens of
// thousands — make the SVG sluggish and drag-zoom janky. Cap the rendered
// series with min/max bucketing: each bucket keeps its lowest and highest
// point in time order, so the load-curve envelope (peaks and troughs) survives
// while the point count stays bounded. Zooming re-buckets the narrower slice,
// so detail returns progressively.
const MAX_POINTS = 800;

function downsample(points: TelemetryPoint[], max: number): TelemetryPoint[] {
	if (points.length <= max) return points;
	const buckets = Math.floor(max / 2);
	const size = points.length / buckets;
	const out: TelemetryPoint[] = [];
	for (let b = 0; b < buckets; b++) {
		const start = Math.floor(b * size);
		const end = Math.min(points.length, Math.floor((b + 1) * size));
		let minI = start;
		let maxI = start;
		for (let i = start + 1; i < end; i++) {
			if (points[i]!.value < points[minI]!.value) minI = i;
			if (points[i]!.value > points[maxI]!.value) maxI = i;
		}
		const lo = Math.min(minI, maxI);
		const hi = Math.max(minI, maxI);
		out.push(points[lo]!);
		if (hi !== lo) out.push(points[hi]!);
	}
	return out;
}

/**
 * Recharts-native telemetry load curve: hourly buckets with drag-to-zoom
 * (ReferenceArea), a Brush scrubber, and Day/Week/Month/Custom range presets.
 * Data fetching stays in the app — this component is presentation-only.
 */
export function TelemetryChart({
	data,
	accent = 'var(--color-accent)',
	unit = ' kWh',
	seriesLabel = 'Valore',
	loading,
	emptyHint = 'Nessun dato per il periodo selezionato',
	range,
	customFrom,
	customTo,
	onRangeChange,
	height = 220,
}: TelemetryChartProps) {
	// Drag-zoom domain (indices into `data`) + in-progress selection refs.
	const [zoom, setZoom] = useState<{ left: number; right: number } | null>(null);
	const [refLeft, setRefLeft] = useState<string | null>(null);
	const [refRight, setRefRight] = useState<string | null>(null);

	const gradientId = useMemo(() => `telemetry-${accent.replace('#', '')}`, [accent]);

	const indexOf = (hour: string) => data.findIndex((d) => d.hour === hour);

	const view = useMemo(() => {
		const slice = !zoom
			? data
			: data.slice(
					Math.max(0, Math.min(zoom.left, zoom.right)),
					Math.min(data.length - 1, Math.max(zoom.left, zoom.right)) + 1,
				);
		return downsample(slice, MAX_POINTS);
	}, [data, zoom]);

	function commitZoom() {
		if (refLeft == null || refRight == null || refLeft === refRight) {
			setRefLeft(null);
			setRefRight(null);
			return;
		}
		const a = indexOf(refLeft);
		const b = indexOf(refRight);
		setRefLeft(null);
		setRefRight(null);
		if (a < 0 || b < 0) return;
		setZoom({ left: a, right: b });
	}

	const isEmpty = !loading && data.length === 0;

	return (
		<div className="space-y-2">
			{/* Range presets + zoom reset */}
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="inline-flex flex-wrap items-center gap-1 rounded-lg border border-border bg-card p-[3px]">
					{RANGE_LABELS.map((r) => (
						<button
							key={r.value}
							type="button"
							onClick={() => onRangeChange(r.value)}
							className={`rounded-md px-2.5 py-1 text-[12px] font-semibold transition-colors ${
								range === r.value
									? 'bg-accent/[0.12] text-accent'
									: 'text-text-muted hover:text-accent'
							}`}
						>
							{r.label}
						</button>
					))}
				</div>
				<div className="flex flex-wrap items-center gap-2">
					{range === 'custom' && (
						<>
							<input
								type="date"
								value={customFrom ?? ''}
								onChange={(e) => onRangeChange('custom', { from: e.target.value, to: customTo })}
								className={inputCls}
								aria-label="Data di inizio"
							/>
							<span className="text-[12px] text-text-muted">→</span>
							<input
								type="date"
								value={customTo ?? ''}
								onChange={(e) => onRangeChange('custom', { from: customFrom, to: e.target.value })}
								className={inputCls}
								aria-label="Data di fine"
							/>
						</>
					)}
					{zoom && (
						<button
							type="button"
							onClick={() => setZoom(null)}
							className="rounded-md border border-border px-2 py-1 text-[12px] text-text-muted hover:text-text"
						>
							Reset zoom
						</button>
					)}
				</div>
			</div>

			<div className="relative select-none">
				<ResponsiveContainer width="100%" height={height}>
					<AreaChart
						data={view}
						onMouseDown={(e) => e?.activeLabel && setRefLeft(String(e.activeLabel))}
						onMouseMove={(e) =>
							refLeft != null && e?.activeLabel && setRefRight(String(e.activeLabel))
						}
						onMouseUp={commitZoom}
					>
						<defs>
							<linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
								<stop offset="0%" stopColor={accent} stopOpacity={0.4} />
								<stop offset="100%" stopColor={accent} stopOpacity={0} />
							</linearGradient>
						</defs>
						<CartesianGrid stroke="#1a3a5c" strokeDasharray="3 3" vertical={false} />
						<XAxis
							dataKey="hour"
							tickFormatter={(v) => fmt(String(v), range)}
							tick={{ fontSize: 11, fill: '#64748b' }}
							tickLine={false}
							axisLine={false}
							minTickGap={24}
						/>
						<YAxis
							tick={{ fontSize: 11, fill: '#64748b' }}
							tickLine={false}
							axisLine={false}
							unit={unit}
							width={52}
						/>
						<Tooltip
							labelFormatter={(v) => fmt(String(v), range === 'day' ? 'week' : range)}
							formatter={(val, _name, item) =>
								[
									`${val}${unit}`,
									item.payload?.sourceLabel
										? `${seriesLabel} · ${item.payload.sourceLabel}`
										: seriesLabel,
								] as [string, string]
							}
							contentStyle={{
								background: '#0f1f35',
								border: '1px solid #1a3a5c',
								borderRadius: 8,
								fontSize: 13,
							}}
							labelStyle={{ color: '#e2e8f0' }}
						/>
						<Area
							type="monotone"
							dataKey="value"
							stroke={accent}
							fill={`url(#${gradientId})`}
							strokeWidth={2}
							isAnimationActive={false}
						/>
						{refLeft != null && refRight != null && (
							<ReferenceArea
								x1={refLeft}
								x2={refRight}
								strokeOpacity={0.3}
								fill={accent}
								fillOpacity={0.1}
							/>
						)}
						{view.length > 1 && (
							<Brush
								dataKey="hour"
								height={20}
								travellerWidth={8}
								stroke="#1a3a5c"
								fill="#0a1628"
								tickFormatter={(v) => fmt(String(v), range)}
							/>
						)}
					</AreaChart>
				</ResponsiveContainer>

				{(isEmpty || loading) && (
					<div className="pointer-events-none absolute inset-0 flex items-center justify-center">
						<span className="rounded-md bg-card/80 px-3 py-1.5 text-[13px] text-text-muted backdrop-blur-sm">
							{loading ? 'Caricamento…' : emptyHint}
						</span>
					</div>
				)}
			</div>
			<p className="text-[11px] text-text-muted">
				Trascina sul grafico per zoomare · aggregazione oraria
			</p>
		</div>
	);
}
